import { LogzioTheme } from '@logz-pkg/enums';
import { BreakpointProvider } from '@logz-pkg/react-hooks';
import React, { ReactNode } from 'react';

// Import existing exports
import { fonts } from './base/fonts';
import { colors, logLevelsColors } from './colors';
import { colorsDark } from './dark-colors';
import { colorsV2 } from './colors-v2';
import { colorsV2Dark } from './dark-colors-v2';
import { DarkTheme } from './dark';
import { darkV2Theme } from './dark-v2.theme';
import { lightV2Theme } from './light-v2.theme';
import { MarketingTheme } from './marketing';
import { iconSize, semanticColor, severityColor, textColor, textSize, textWeight } from './selectors';

// Import new theme system
import { ThemeContext, ThemeProvider } from './theme-context';

// Re-export existing exports
export type { Color } from './colors';
export type { ColorPalette, ColorV2 } from './colors-v2';
export {
  colors,
  colorsV2,
  colorsDark,
  colorsV2Dark,
  DarkTheme,
  darkV2Theme,
  fonts,
  iconSize,
  lightV2Theme,
  logLevelsColors,
  MarketingTheme,
  semanticColor,
  severityColor,
  textColor,
  textSize,
  textWeight,
};

export { ThemeContext, ThemeProvider };

const withTry =
  method =>
  (...args) => {
    try {
      return method(...args);
    } catch (e) {
      return undefined;
    }
  };

const is = n => n !== undefined && n !== null;
const value = (n, props) => (typeof n === 'function' ? withTry(n)(props) : n);
const get = (obj, path, fallback) => path.reduce((a, b) => (a && a[b] ? a[b] : null), obj) || fallback;

export const withThemesByProps = (...byProps) => {
  const styleValues = byProps.pop();

  return props =>
    get(
      value(styleValues, props),
      byProps.map(prop => props[prop]),
      null,
    );
};

export const getThemeObject = (theme: LogzioTheme) =>
  ({
    [LogzioTheme.Light]: lightV2Theme,
    [LogzioTheme.Dark]: darkV2Theme,
  }[theme] || lightV2Theme);

export const withThemesFallback =
  (...fbChain) =>
  props =>
    fbChain.find(fb => is(value(fb, props)));

interface ILogzioThemeProviderProps {
  theme?: any;
  initialTheme?: LogzioTheme;
  children: ReactNode;
  targetDocument?: Document;
}

export const LogzioThemeProvider: React.FC<ILogzioThemeProviderProps> = ({
  children,
  initialTheme,
  theme,
  targetDocument,
}) => (
  <BreakpointProvider>
    <ThemeProvider initialTheme={initialTheme} theme={theme} targetDocument={targetDocument}>
      {children}
    </ThemeProvider>
  </BreakpointProvider>
);
